/**
 * Owner: Masa Bayu
 * File: reader-profile-data.ts
 * Date:Time: 2026-10-18 23:05
 * Description: §393 — lapisan data Profil Reader: agregasi sumber nyata
 *   (authSession hub + createLibraryModel + wishlist + bookmark/highlight
 *   localStorage + follows + alerts + prefs pembaca + locale). Tiada rekaan.
 *   §457 F6-C — identiti membawa `hasPassword` (medan hub) untuk gerbang
 *   pembatalan akaun §453.
 *   §458 — KPI "Books owned" dibaca dari `ownedCount` (projeksi `purchased`),
 *   bukan rak terfilter; seed rekaan daripada katalog sudah dibuang.
 */

import type { AuthSessionView } from '../auth/session-types';
import { createLibraryModel } from '../library/shared/createLibraryModel';
import type { LibraryShelfCard } from '../library/shared/library-types';
import { listNaturalBookmarks } from '../reader/natural/natural-reader-bookmarks';
import { listNaturalHighlights } from '../reader/natural/natural-reader-highlights';
import {
  getNaturalReaderPrefs,
  type NaturalReaderPrefs,
} from '../reader/natural/natural-reader-prefs';
import { listLandingAlerts, type LandingAlert } from '../home/marketplace/landing/landing-alerts';
import { getAuthorFollowSnapshot } from '../author/public/public-author-follow-store';
import {
  getMarketplaceLocaleProfile,
  type MarketplaceLocaleProfile,
} from '../home/marketplace/marketplace-locale';
import type { QxreadWishlistItem } from '@qx/qxread-core';

export type ReaderProfileSection = 'overview' | 'library' | 'orders' | 'publishing' | 'settings';

export const READER_PROFILE_SECTIONS: readonly ReaderProfileSection[] = Object.freeze([
  'overview',
  'library',
  'orders',
  'publishing',
  'settings',
]);

/** `/account/<seksyen>` ialah satu-satunya cara memilih seksyen supaya pautan
 *  tersimpan dan butang belakang berfungsi; nama tak dikenali jatuh ke Ringkasan. */
export function sectionFromAccountPath(path: string | undefined): ReaderProfileSection {
  const id = (path ?? '/account').split('/')[2] ?? '';
  return (READER_PROFILE_SECTIONS as readonly string[]).includes(id)
    ? (id as ReaderProfileSection)
    : 'overview';
}

/** Lencana dibaca terus daripada `role` yang diisytiharkan hub — tiada gabungan rekaan. */
export type ReaderProfileBadge = 'reader' | 'publisher' | 'admin';

export interface ReaderProfileIdentity {
  readonly displayName: string;
  readonly email: string;
  readonly monogram: string;
  readonly badge: ReaderProfileBadge;
  /** §457 F6-C — `null` = tiada sesi; `false` = Google-sahaja (S3); `true` = ada kata laluan. */
  readonly hasPassword: boolean | null;
}

export interface ReaderProfileStats {
  readonly owned: number;
  readonly reading: number;
  readonly completed: number;
  readonly wishlist: number;
  readonly bookmarks: number;
  readonly highlights: number;
  readonly follows: number;
}

export interface ReaderProfileData {
  readonly identity: ReaderProfileIdentity;
  readonly stats: ReaderProfileStats;
  readonly continueReading: readonly LibraryShelfCard[];
  readonly shelf: readonly LibraryShelfCard[];
  readonly completed: readonly LibraryShelfCard[];
  readonly wishlist: readonly QxreadWishlistItem[];
  readonly alerts: readonly LandingAlert[];
  readonly prefs: NaturalReaderPrefs;
  readonly locale: MarketplaceLocaleProfile;
}

function monogramFrom(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return '?';
  if (parts.length === 1) return parts[0]!.slice(0, 1).toUpperCase();
  return `${parts[0]!.slice(0, 1)}${parts[1]!.slice(0, 1)}`.toUpperCase();
}

/**
 * Satu lencana daripada satu `role` hub. `publisher` tidak dipaparkan sebagai
 * `reader` dan sebaliknya: peranan di pelayan eksklusif, jadi lencana gabungan
 * akan menjadi dakwaan yang tiada sumbernya.
 */
function badgeOf(roles: readonly string[] | undefined): ReaderProfileBadge {
  if ((roles ?? []).includes('admin')) return 'admin';
  return (roles ?? []).includes('publisher') ? 'publisher' : 'reader';
}

/**
 * §457 F6-C — `hasPassword` ialah medan tambahan yang dieksport modul sesi hub
 * (perisai F3). Ia dibaca tanpa melebarkan `AuthSessionView`: `null` = tiada
 * sesi atau identiti bukan hub, supaya paparan tidak pernah meneka.
 */
function hasPasswordFrom(session: AuthSessionView | null): boolean | null {
  if (session === null) return null;
  const flag = (session as { readonly hasPassword?: unknown }).hasPassword;
  return typeof flag === 'boolean' ? flag : null;
}

export function buildReaderProfileData(session: AuthSessionView | null): ReaderProfileData {
  const model = createLibraryModel();
  const account = session?.account;
  const displayName = account?.profile?.displayName?.trim() || account?.user?.email || 'Pembaca';
  const email = account?.user?.email ?? '';
  // Pengesahan §453 mesti akaun sebenar — identiti tanpa e-mel (anon / separa)
  // tidak boleh mencuba pemadaman, walaupun medan `hasPassword` wujud.
  const signedIn = account !== undefined && email.length > 0;
  const ownedIds = [...model.shelf, ...model.continueReading].map((c) => c.item.publicationId);
  const uniqueIds = [...new Set(ownedIds)];
  const bookmarks = uniqueIds.reduce((sum, id) => sum + listNaturalBookmarks(id).length, 0);
  const highlights = uniqueIds.reduce((sum, id) => sum + listNaturalHighlights(id).length, 0);
  return {
    identity: {
      displayName,
      email,
      monogram: monogramFrom(displayName),
      badge: badgeOf(account?.roles),
      hasPassword: signedIn ? hasPasswordFrom(session) : null,
    },
    stats: {
      owned: model.ownedCount,
      reading: model.continueReading.length,
      completed: model.completed.length,
      wishlist: model.wishlist.length,
      bookmarks,
      highlights,
      follows: getAuthorFollowSnapshot().length,
    },
    continueReading: model.continueReading,
    shelf: model.shelf,
    completed: model.completed,
    wishlist: model.wishlist,
    alerts: listLandingAlerts(),
    prefs: getNaturalReaderPrefs(),
    locale: getMarketplaceLocaleProfile(),
  };
}
